import { useEffect, useState } from 'react';
import useSaveSimulationCustomerGranteesMutation from './useSaveSimulationCustomerGranteesMutation';
import { formatProjectBeforeSubmit } from '@@viewHelpers/format.jsx';
import { isNilOrEmpty } from '@@serverHelpers/index.mjs';
import {
  assocPath,
  compose,
  concat,
  defaultTo,
  find,
  findIndex,
  path,
  prop,
  propEq,
  propOr
} from 'ramda';
import { getZipCodeRules } from '@@viewHelpers/zipCode';
import { GranteeTypeEnum } from '@fasstech/oip-core/entities/Grantee/enums';
import { useProcess } from '@fasstech/oip-starter-utils/app';
import usePavillonSearchCustomerSalesRightsMutation from './usePavillonSearchCustomerSalesRightsMutation.js';
import trackLeadSimulationExtra from '../extra/trackLeadSimulationExtra';
import { useAnalytics } from '@@app/context/analytics-context';
import { formattedRegime } from '../../../_common/common/helpers/formattedRegime.mjs';
import { getCaisseFromValue } from '@@viewCommonComponents/forms/options';

const useSimulationCustomerGranteesForm = ({
  jobs,
  professionalSituations,
  form,
  getMiddleBoolean,
  middleFeatures,
  getMiddleText
}) => {
  const [loading, setLoading] = useState(false);
  const [unauthorizedModalMessage, setUnauthorizedModalMessage] = useState('');
  const [isUnauthorized, setIsUnauthorized] = useState(false);
  const { processHandlers, context } = useProcess();

  const { onFormSubmit, loading: isFetchingForm } = useSaveSimulationCustomerGranteesMutation();

  const { onFormSubmit: onPavillonSearchCustomerSalesRights } =
    usePavillonSearchCustomerSalesRightsMutation();
  const { trackEvent } = useAnalytics();

  useEffect(() => {
    setIsUnauthorized(false);
  }, [
    form.watch('customer.firstName'),
    form.watch('customer.lastName'),
    form.watch('customer.birthDate')
  ]);
  const onSubmit = (values) => {
    if (loading) return;
    trackLeadSimulationExtra(trackEvent, getMiddleText);
    setLoading(true);
    let formattedValues = formatProjectBeforeSubmit(values, getMiddleText);
    const customerCSPExternalId = path(['customer', 'CSPs', 0, 'externalId'], values);

    if (!isNilOrEmpty(customerCSPExternalId)) {
      const CSPLabel = compose(
        prop('label'),
        defaultTo({}),
        find(propEq('value', customerCSPExternalId))
      )(jobs);

      formattedValues = assocPath(['customer', 'CSPs', 0, 'label'], CSPLabel, formattedValues);
    }

    const spouseIndex = compose(
      findIndex(propEq('type', GranteeTypeEnum.SPOUSE)),
      propOr([], 'grantees')
    )(values);
    const spouseCSPExternalId = path(['grantees', spouseIndex, 'CSPs', 0, 'externalId'], values);

    if (!isNilOrEmpty(spouseCSPExternalId)) {
      const CSPLabel = compose(
        prop('label'),
        defaultTo({}),
        find(propEq('value', spouseCSPExternalId))
      )(jobs);

      formattedValues = assocPath(
        ['grantees', spouseIndex, 'CSPs', 0, 'label'],
        CSPLabel,
        formattedValues
      );
    }

    const customerCategoryLvl1CSPExternalId = path(
      ['customer', 'CSPs', 0, 'categoryLvl1CSPExternalId'],
      values
    );

    if (!isNilOrEmpty(customerCategoryLvl1CSPExternalId)) {
      const CSPCategoryLvl1Label = compose(
        prop('label'),
        defaultTo({}),
        find(propEq('value', customerCategoryLvl1CSPExternalId))
      )(professionalSituations);

      formattedValues = assocPath(
        ['customer', 'CSPs', 0, 'categoryLvl1Label'],
        CSPCategoryLvl1Label,
        formattedValues
      );
    }

    const spouseCategoryLvl1CSPExternalId = path(
      ['grantees', spouseIndex, 'CSPs', 0, 'categoryLvl1CSPExternalId'],
      values
    );

    if (!isNilOrEmpty(spouseCategoryLvl1CSPExternalId)) {
      const CSPCategoryLvl1Label = compose(
        prop('label'),
        defaultTo({}),
        find(propEq('value', spouseCategoryLvl1CSPExternalId))
      )(professionalSituations);

      formattedValues = assocPath(
        ['grantees', spouseIndex, 'CSPs', 0, 'categoryLvl1Label'],
        CSPCategoryLvl1Label,
        formattedValues
      );
    }

    if (
      getMiddleBoolean('ADHERENT_PROFESSION_EST_NECESSAIRE') &&
      getMiddleBoolean('ADHERENT_PROFESSION_EST_DEMANDE_DONNEES_CONTRAT') &&
      getMiddleBoolean('ADHERENT_CAISSE_PRO_EST_NECESSAIRE') &&
      getMiddleBoolean('ADHERENT_CAISSE_PRO_EST_DEMANDE_DONNEES_CONTRAT')
    ) {

      const caissePro = getCaisseFromValue(
        path(['customer', 'CSPs', 0, 'externalId'], values),
        jobs
      );

      formattedValues = assocPath(
        ['customer', 'socialSecurities', 0, 'regime'],
        concat('REGIME_PRO_', formattedRegime(caissePro)),
        formattedValues
      );

      formattedValues = assocPath(
        ['customer', 'socialSecurities', 0, 'caissePro'],
        undefined,
        formattedValues
      );
    }

    onPavillonSearchCustomerSalesRights({ customer: formattedValues.customer }, (ok, err, data) => {
      if (ok && !err) {
        setIsUnauthorized(false);
        onFormSubmit(formattedValues, (ok, err) => {
          if (ok && !err) {
            processHandlers.onNext({
              context: {
                projectId: context?.projectId
              }
            });
          }
        });
      } else {
        setLoading(false);
        if (err && propEq('code', 'SALES_UNAUTHORIZED', data)) {
          setUnauthorizedModalMessage(err);
          setIsUnauthorized(true);
        }
      }
    });
  };

  const { ADHERENT_CODE_POSTAL_FILTRES_GEOGRAPHIQUES = [] } = middleFeatures;
  const postalCodeFranceMetro = getZipCodeRules(
    'France Métropolitaine',
    ADHERENT_CODE_POSTAL_FILTRES_GEOGRAPHIQUES
  );
  const postalCodeDROM = getZipCodeRules('DROM', ADHERENT_CODE_POSTAL_FILTRES_GEOGRAPHIQUES);

  const $onPrevClick = () => {
    processHandlers.onPrev({
      context: {
        projectId: context?.projectId
      }
    });
  };

  const { isValid } = form.formState;

  const displayAdditionalInfo =
    (getMiddleBoolean('ADHERENT_REGIME_EST_NECESSAIRE') &&
      getMiddleBoolean('ADHERENT_REGIME_EST_DEMANDE_DONNEES_CONTRAT')) ||
    (getMiddleBoolean('ADHERENT_TEL_MOBILE_EST_NECESSAIRE') &&
      getMiddleBoolean('ADHERENT_TEL_MOBILE_EST_DEMANDE_DONNEES_CONTRAT')) ||
    (getMiddleBoolean('ADHERENT_TEL_FIXE_EST_NECESSAIRE') &&
      getMiddleBoolean('ADHERENT_TEL_FIXE_EST_DEMANDE_DONNEES_CONTRAT')) ||
    (getMiddleBoolean('ADHERENT_EMAIL_EST_NECESSAIRE') &&
      getMiddleBoolean('ADHERENT_EMAIL_EST_DEMANDE_DONNEES_CONTRAT')) ||
    (getMiddleBoolean('ADHERENT_NSS_EST_NECESSAIRE') &&
      getMiddleBoolean('ADHERENT_NSS_EST_DEMANDE_DONNEES_CONTRAT'));

  const displayActivityInfo =
    (getMiddleBoolean('ADHERENT_SIT_PRO_EST_NECESSAIRE') &&
      getMiddleBoolean('ADHERENT_SIT_PRO_EST_DEMANDE_DONNEES_CONTRAT')) ||
    (getMiddleBoolean('ADHERENT_CSP_EST_NECESSAIRE') &&
      getMiddleBoolean('ADHERENT_CSP_EST_DEMANDE_DONNEES_CONTRAT')) ||
    (getMiddleBoolean('ADHERENT_PROFESSION_EST_NECESSAIRE') &&
      getMiddleBoolean('ADHERENT_PROFESSION_EST_DEMANDE_DONNEES_CONTRAT')) ||
    (getMiddleBoolean('ADHERENT_PROFESSION_EST_NECESSAIRE') &&
      getMiddleBoolean('ADHERENT_PROFESSION_EST_DEMANDE_DONNEES_CONTRAT') &&
      getMiddleBoolean('ADHERENT_CAISSE_PRO_EST_NECESSAIRE') &&
      getMiddleBoolean('ADHERENT_CAISSE_PRO_EST_DEMANDE_DONNEES_CONTRAT')) ||
    (getMiddleBoolean('ADHERENT_RANG_EST_NECESSAIRE') &&
      getMiddleBoolean('ADHERENT_RANG_EST_DEMANDE_DONNEES_CONTRAT')) ||
    (getMiddleBoolean('ADHERENT_ACTIVITE_EST_NECESSAIRE') &&
      getMiddleBoolean('ADHERENT_ACTIVITE_EST_DEMANDE_DONNEES_CONTRAT')) ||
    (getMiddleBoolean('ADHERENT_REVENU_ANNUEL_EST_NECESSAIRE') &&
      getMiddleBoolean('ADHERENT_REVENU_ANNUEL_EST_DEMANDE_DONNEES_CONTRAT')) ||
    (getMiddleBoolean('ADHERENT_ID_TNS_EST_NECESSAIRE') &&
      getMiddleBoolean('ADHERENT_ID_TNS_EST_DEMANDE_DONNEES_CONTRAT')) ||
    getMiddleBoolean('ADHERENT_CSS_EST_NECESSAIRE');

  return {
    onSubmit,
    unauthorizedModalMessage,
    setUnauthorizedModalMessage,
    isUnauthorized,
    isFetchingForm,
    postalCodeFranceMetro,
    postalCodeDROM,
    $onPrevClick,
    isValid,
    displayAdditionalInfo,
    displayActivityInfo
  };
};

export default useSimulationCustomerGranteesForm;
